import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nx/angular';
import * as RolesActions from './roles.actions';
import * as UserActions from '../users/users.actions';
import { Store } from '@ngrx/store';
import { RolesService } from '@boma/supplier-portal/shared/services';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { RoleAuthTarget } from '@boma/client-shared';
import { hasRole } from '@boma/shared/common';
import { of } from 'rxjs';

@Injectable()
export class RolesEffects {
  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.loadRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          return this.rolesService
            .listRoles(
              user.selectedOrganizationId,
              '1',
              '999',
              '',
              action.target.valueOf().toString()
            )
            .pipe(
              map((response) => {
                console.log('LIST RESPONSE', response);
                const entity = response.items.filter((i) =>
                  // @ts-ignore
                  hasRole(RoleAuthTarget.Entity, i.target)
                );
                const location = response.items.filter((i) => {
                  const isLocation =
                    (i.target & RoleAuthTarget.Location) ===
                    RoleAuthTarget.Location;
                  // @ts-ignore
                  return isLocation;
                });
                const vehicle = response.items.filter((i) =>
                  // @ts-ignore
                  hasRole(RoleAuthTarget.Vehicle, i.target)
                );
                return [
                  RolesActions.loadEntityRolesSuccess({
                    payload: entity,
                  }),
                  RolesActions.loadLocationRolesSuccess({
                    payload: location,
                  }),
                  RolesActions.loadVehicleRolesSuccess({
                    payload: vehicle,
                  }),
                ];
              }),
              mergeMap((e) => e)
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.loadRolesFailure({ error });
        },
      })
    )
  );

  getUserAdminRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.loadAssignedAdminRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          if (action.userId === '') action.userId = user.id;

          return this.rolesService
            .getUserAdminRoles(user.selectedOrganizationId, action.userId)
            .pipe(
              map((response) => {
                return RolesActions.loadAssignedAdminRolesSuccess({
                  payload: response.roles,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.loadAssignedAdminRolesFailure({ error });
        },
      })
    )
  );

  setUserAdminRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.applyAssignedAdminRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          //console.log(action);
          const { user } = state; // 'organization'
          return this.rolesService
            .setUserAdminRoles(
              user.selectedOrganizationId,
              action.userId,
              action.roles
            )
            .pipe(
              switchMap((response) => {
                const entity = response.roles.filter((i) =>
                  // @ts-ignore
                  hasRole(RoleAuthTarget.Entity, i.target)
                );
                const location = response.roles.filter((i) => {
                  // @ts-ignore
                  return hasRole(RoleAuthTarget.Location, i.target);
                });
                const vehicle = response.roles.filter((i) =>
                  // @ts-ignore
                  hasRole(RoleAuthTarget.Vehicle, i.target)
                );
                return of(
                  RolesActions.loadEntityRolesSuccess({
                    payload: entity,
                  }),
                  RolesActions.loadLocationRolesSuccess({
                    payload: location,
                  }),
                  RolesActions.loadVehicleRolesSuccess({
                    payload: vehicle,
                  })
                );
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.applyAssignedAdminRolesFailure({ error });
        },
      })
    )
  );

  setUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.applyUserRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          console.log(state);
          const { user } = state; // 'organization'
          return this.rolesService
            .setUserRoles(
              user.selectedOrganizationId,
              action.userId,
              action.roles
            )
            .pipe(
              switchMap((response) => {
                return of(
                  UserActions.getUserEntity({
                    userId: action.userId,
                    entityId: user.selectedOrganizationId,
                  }),
                  RolesActions.applyAssignedAdminRolesSuccess({
                    payload: response.roles,
                  })
                );
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.applyAssignedAdminRolesFailure({ error });
        },
      })
    )
  );

  createEntityRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.createEntityRole),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          return this.rolesService
            .createEntityRole({ request: action.request })
            .pipe(
              map((response) => {
                return RolesActions.modifyEntityRolesSuccess({
                  payload: response,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.modifyEntityRolesFailure({ error });
        },
      })
    )
  );

  loadRoleDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.loadEntityRoleDetails),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          console.log(action, state);
          return this.rolesService
            .loadEntityRoleDetails(action.id, user.selectedOrganizationId)
            .pipe(
              map((response) => {
                return RolesActions.loadEntityRoleDetailsSuccess({
                  role: response,
                });
              })
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.loadEntityRoleDetailsFailure({ error });
        },
      })
    )
  );

  updateEntityRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.updateEntityRole),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          return this.rolesService
            .updateEntityRole({
              roleId: action.roleId,
              request: action.request,
            })
            .pipe(
              map((response) => {
                return RolesActions.modifyEntityRolesSuccess({
                  payload: response,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return RolesActions.modifyEntityRolesFailure({ error });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private rolesService: RolesService
  ) {}

  private _processRoleResponse() {}
}
